import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapQueryResult, mapTestDetailDto, mapTestListItemDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mapMetricSorts } from '@/shared/data/metricSortFields';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import { buildSlugFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  QueryFilter,
  QueryResult,
  QuerySort,
  TestDetailDto,
  TestFilterCountsDto,
  TestRunStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryTests = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    search?: string;
    metricsWindowDays?: number;
    metricsStartDay?: string;
    metricsEndDay?: string;
    previousMetricsStartDay?: string;
    previousMetricsEndDay?: string;
    include?: TestInclude;
  },
): Promise<QueryResult<TestListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts
    ? mapMetricSorts(input.sorts)
    : [{ field: 'createdAt', direction: 'desc' }];
  try {
    const result = await apiClient.post<QueryResult<TestListItemDto>>(
      'tests/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts,
        filters: combinedFilters,
        search: input?.search,
        metricsWindowDays: input?.metricsWindowDays,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
        previousMetricsStartDay: input?.previousMetricsStartDay,
        previousMetricsEndDay: input?.previousMetricsEndDay,
        include: input?.include,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapTestListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
    metricsStartDay?: string;
    metricsEndDay?: string;
  },
): Promise<TestSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestSummaryDto>(
      'tests/summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
        metricsStartDay: input?.metricsStartDay,
        metricsEndDay: input?.metricsEndDay,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestStatusSummary = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: QueryFilter[];
  },
): Promise<TestRunStatusSummaryDto> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  const sorts = input?.sorts ? mapMetricSorts(input.sorts) : [];
  try {
    return await apiClient.post<TestRunStatusSummaryDto>(
      'tests/status-summary',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 1,
        sorts,
        filters: combinedFilters,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryTestFilterCounts = async (
  projectId: string,
  organisationId: string | null | undefined,
  input: {
    search?: string;
    metricsStartDay?: string;
    metricsEndDay?: string;
    filters: {
      tests: GridFilters;
      suites: GridFilters;
      statuses: GridFilters;
      tags: GridFilters;
    };
  },
): Promise<TestFilterCountsDto> => {
  const token = ensureToken();
  try {
    return await apiClient.post<TestFilterCountsDto>(
      'tests/filter-counts',
      {
        search: input.search,
        metricsStartDay: input.metricsStartDay,
        metricsEndDay: input.metricsEndDay,
        filters: {
          tests: mergeProjectFilters(input.filters.tests, projectId),
          suites: mergeProjectFilters(input.filters.suites, projectId),
          statuses: mergeProjectFilters(input.filters.statuses, projectId),
          tags: mergeProjectFilters(input.filters.tags, projectId),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getTestBySlug = async (
  slug: string | null,
  projectId?: string | null,
  organisationId?: string | null,
): Promise<TestDetailDto | null> => {
  if (!slug) {
    return null;
  }
  const token = ensureToken();
  const filters = buildSlugFilters(slug);
  try {
    const payload = await apiClient.post<QueryResult<TestListItemDto>>(
      'tests/query',
      { page: 1, pageSize: 1, filters },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    const mapped = mapQueryResult(payload, mapTestListItemDto);
    if (mapped.items.length > 0) {
      return await getTestById(mapped.items[0].id, organisationId);
    }
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw toApiError(err);
  }
};

export const getTestById = async (
  id: string,
  organisationId?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<TestDetailDto>(`tests/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createTest = async (
  input: {
    name: string;
    projectId: string;
    featureIds?: string[];
    suiteIds?: string[];
  },
  organisationId?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name.trim(),
    projectId: input.projectId.trim(),
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
  };
  try {
    const result = await apiClient.post<TestDetailDto>('tests', body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateTest = async (
  input: {
    id: string;
    name?: string;
    featureIds?: string[];
    suiteIds?: string[];
    tags?: string[];
  },
  organisationId?: string | null,
): Promise<TestDetailDto> => {
  const token = ensureToken();
  const body = {
    name: input.name?.trim(),
    featureIds: input.featureIds,
    suiteIds: input.suiteIds,
    tags: input.tags,
  };
  try {
    const result = await apiClient.patch<TestDetailDto>(`tests/${input.id}`, body, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return mapTestDetailDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export type TestInclude = {
  project?: boolean;
  features?: boolean;
  suites?: { ids?: boolean; items?: boolean; primary?: boolean };
  latestRun?: boolean;
  executionStatusCounts?: boolean;
  dailyHistory?: boolean;
  executionSummaries?: boolean;
  metrics?: boolean;
  metricDeltas?: boolean;
};

export const deleteTest = async (
  input: { id: string },
  organisationId?: string | null,
): Promise<{ id: string }> => {
  const token = ensureToken();
  try {
    await apiClient.post<undefined>(`tests/${input.id}/delete`, undefined, {
      token,
      retries: 1,
      orgId: organisationId ?? undefined,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};
